/**
 * Remember why someone arrived, before anything can redirect them away.
 *
 * The connector flow lands on /account?new_key=…&for=…&return_to=…. Reading
 * that inside the account screen was too late: someone who is not signed in
 * gets sent to the login route first, the query string goes with it, and they
 * arrive at a perfectly ordinary account page with no idea why — which is
 * exactly what "it just took me to account.fluxuro.ai and nothing happened"
 * looks like from the outside.
 *
 * Captured at boot instead, before routing decides anything, and held until
 * the account screen is ready to act on it.
 */
const KEY = "uro.connectorIntent";

const HANDOFF_ACTION = "https://llm-mcp.fluxuro.ai/authorize/handoff";
const MAX_AGE_MS = 30 * 60 * 1000;

function handoffTarget(value) {
  if (typeof value !== "string") throw new Error("Invalid connector return address.");
  const url = new URL(value);
  const entries = Array.from(url.searchParams.entries());
  if (
    url.origin !== "https://llm-mcp.fluxuro.ai" ||
    url.pathname !== "/authorize/handoff" ||
    url.username || url.password || url.href.includes("#") ||
    entries.length !== 1 || entries[0][0] !== "h" || !entries[0][1].trim()
  ) throw new Error("Invalid connector return address.");
  return { url: url.href, handle: entries[0][1] };
}

export function clearConnectorIntent() {
  try { sessionStorage.removeItem(KEY); } catch { /* storage may be unavailable */ }
}

/** Revalidate on every read; rendering or refreshing must not consume intent. */
export function peekConnectorIntent() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw);
    const age = Date.now() - intent?.at;
    if (!intent || typeof intent.source !== "string" || !intent.source.trim() ||
        !Number.isFinite(intent.at) || age < 0 || age > MAX_AGE_MS ||
        (intent.who != null && typeof intent.who !== "string")) {
      throw new Error("Invalid connector intent.");
    }
    return {
      source: intent.source,
      who: (intent.who || "").toLowerCase(),
      returnTo: intent.returnTo == null ? null : handoffTarget(intent.returnTo).url,
      at: intent.at,
    };
  } catch {
    clearConnectorIntent();
    return null;
  }
}

export function captureConnectorIntent() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("new_key")) return;

    let intent = null;
    try {
      if (["new_key", "for", "return_to"].some(name => params.getAll(name).length > 1)) {
        throw new Error("Ambiguous connector intent.");
      }
      const back = params.get("return_to");
      intent = {
        source: params.get("new_key"),
        who: (params.get("for") || "").toLowerCase(),
        returnTo: back == null ? null : handoffTarget(back).url,
        at: Date.now(),
      };
    } catch {
      /* Ignore invalid input without replacing an existing valid connection. */
    }

    // If storage fails, leave the URL intact so the original handoff can retry.
    if (intent) sessionStorage.setItem(KEY, JSON.stringify(intent));

    ["new_key", "for", "return_to"].forEach(name => params.delete(name));
    const query = params.toString();
    window.history.replaceState(window.history.state, "", window.location.pathname +
      (query ? "?" + query : "") + window.location.hash);
  } catch {
    /* private mode, or no storage — the manual path still works */
  }
}

// Compatibility for existing callers; intent is now cleared only by an action.
export const takeConnectorIntent = peekConnectorIntent;

/** Client identity comes from the pending authorization, never URL hints. */
export async function getConnectorContext(returnTo) {
  const target = handoffTarget(returnTo);
  try {
    const endpoint = new URL("https://llm-mcp.fluxuro.ai/authorize/context");
    endpoint.searchParams.set("h", target.handle);
    const response = await fetch(endpoint.href, {
      cache: "no-store", credentials: "omit", redirect: "error",
    });
    if (!response.ok) throw new Error("Connector context unavailable.");
    const data = await response.json();
    if (!data || typeof data.name !== "string" || !data.name.trim() ||
        (data.host !== null && (typeof data.host !== "string" || !data.host.trim())) ||
        typeof data.verified !== "boolean") {
      throw new Error("Invalid connector context.");
    }
    let redirectScheme;
    if (data.redirectOrigin === null) {
      if (typeof data.redirectScheme !== "string" || !/^[a-z][a-z0-9+.-]*:$/i.test(data.redirectScheme)) {
        throw new Error("Invalid connector redirect scheme.");
      }
      redirectScheme = data.redirectScheme.toLowerCase();
      if (["http:", "https:", "javascript:", "data:", "vbscript:", "file:"].includes(redirectScheme)) {
        throw new Error("Unsafe connector redirect scheme.");
      }
    } else {
      const origin = new URL(data.redirectOrigin);
      if (typeof data.redirectOrigin !== "string" ||
          !["http:", "https:"].includes(origin.protocol) || origin.origin !== data.redirectOrigin) {
        throw new Error("Invalid connector redirect origin.");
      }
    }
    return {
      name: data.name, host: data.host, verified: data.verified,
      redirectOrigin: data.redirectOrigin, returnTo: target.url,
      ...(redirectScheme ? { redirectScheme } : {}),
    };
  } catch {
    throw new Error("We couldn't confirm which app is connecting. Please try again.");
  }
}

/** Native POST carries the key in the body, never the address bar or storage. */
export function handoffToConnector(returnTo, key) {
  const { handle } = handoffTarget(returnTo);
  if (typeof key !== "string" || !key.trim()) throw new Error("Choose a key to connect.");
  const form = document.createElement("form");
  form.method = "POST";
  form.action = HANDOFF_ACTION;
  form.hidden = true;
  for (const [name, value] of [["h", handle], ["key", key]]) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  try {
    form.submit();
    clearConnectorIntent();
  } finally {
    form.remove();
  }
}
